import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Camera, CameraOff, Check, RotateCcw, Upload,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { uploadPodPhoto, DELIVERY_ERRORS } from '../../api/delivery';
import Button from '../../components/Button';
import DriverShell from './DriverShell';
import { useDriverOrder } from './useDriverOrder';
import cardStyles from './DriverCards.module.css';
import styles from './DriverProofOfDelivery.module.css';

function friendlyError(err) {
  return DELIVERY_ERRORS[err.message] || err.message || 'Could not upload the photo — try again.';
}

const MAX_PHOTO_DIMENSION = 1600;

// Native camera apps often capture at the sensor's full resolution (12MP+,
// sometimes 50MP+) — displaying or uploading that raw file directly is what
// triggers Android/Chrome's "unable to complete operation due to memory"
// toast. Downscale to a sane max dimension before we touch it further.
function shrinkImage(blob, maxDimension) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDimension / Math.max(img.naturalWidth, img.naturalHeight));
      if (scale === 1) {
        URL.revokeObjectURL(objectUrl);
        resolve(blob);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);
      canvas.toBlob((resized) => {
        if (resized) resolve(resized); else reject(new Error('Could not process that photo — try again.'));
      }, 'image/jpeg', 0.85);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not process that photo — try again.'));
    };
    img.src = objectUrl;
  });
}

export default function DriverProofOfDelivery() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const { notify } = useOrders();
  const navigate = useNavigate();
  const { order } = useDriverOrder(orderId);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const captureGuardRef = useRef(false);
  const previewUrlRef = useRef(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setCameraReady(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch {
      setCameraError('Live camera is not available on this browser. Use your camera app instead.');
    }
  }, []);

  useEffect(() => {
    if (!order) return undefined;
    startCamera();
    return () => stopCamera();
  }, [order, startCamera, stopCamera]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  // Samsung Internet (and some other mobile browsers) fire a spurious
  // `popstate` — or in Samsung Internet's case, a full page reload — when
  // the native camera/file picker (opened via the hidden capture input
  // below) closes. React Router treats a popstate as the user pressing
  // Back, bouncing the app to whatever screen it arrived from. This guard
  // only helps with the popstate case; a genuine reload (Samsung Internet)
  // can't be prevented from JS, which is why the live in-page camera above
  // is the primary capture path and this file-input flow is just a
  // fallback for browsers where getUserMedia isn't available.
  useEffect(() => {
    const onPopState = () => {
      if (!captureGuardRef.current) return;
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const openFilePicker = () => {
    captureGuardRef.current = true;
    window.history.pushState(null, '', window.location.href);
    fileInputRef.current?.click();
    window.addEventListener('focus', () => {
      setTimeout(() => { captureGuardRef.current = false; }, 500);
    }, { once: true });
  };

  const uploadAndContinue = async (blob) => {
    setUploading(true);
    setUploadError('');
    try {
      const podPhotoKey = await uploadPodPhoto(order.id, blob, user.idToken);
      navigate(`/driver/rides/${order.id}/complete`, { state: { order, podPhotoKey } });
    } catch (err) {
      const message = friendlyError(err);
      setUploadError(message);
      notify(message);
      setUploading(false);
    }
  };

  const setCapturedPhoto = (blob) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const url = URL.createObjectURL(blob);
    previewUrlRef.current = url;
    setPhotoPreview(url);
    setPhotoFile(blob);
    setUploadError('');
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    stopCamera();
    canvas.toBlob((blob) => {
      if (blob) setCapturedPhoto(blob);
    }, 'image/jpeg', 0.9);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    captureGuardRef.current = false;
    if (!file) return;
    try {
      const shrunk = await shrinkImage(file, MAX_PHOTO_DIMENSION);
      setCapturedPhoto(shrunk);
    } catch (err) {
      setUploadError(err.message);
      notify(err.message);
    }
  };

  const retake = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setUploadError('');
    if (cameraError) {
      openFilePicker();
    } else {
      startCamera();
    }
  };

  const submitPhoto = () => {
    if (photoFile) uploadAndContinue(photoFile);
  };

  const showVideo = !photoPreview && !cameraError;

  return (
    <DriverShell>
      <header className={cardStyles.header}>
        <button className={cardStyles.backBtn} onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <h1 className={cardStyles.headerTitle}>Proof of Delivery</h1>
      </header>

      {order && (
        <>
          <main className={cardStyles.content}>
            <p className={styles.instruction}>
              Capture a clear photo of the delivered order at the customer's doorstep.
            </p>

            <div className={styles.photoBox}>
              {photoPreview && (
                <img src={photoPreview} alt="Captured proof of delivery" className={styles.previewImage} />
              )}

              <video
                ref={videoRef}
                className={styles.videoFeed}
                style={{ display: showVideo ? 'block' : 'none' }}
                playsInline
                muted
                autoPlay
              />

              {showVideo && !cameraReady && !uploading && (
                <div className={styles.photoPlaceholder}>
                  <Camera size={30} strokeWidth={1.5} />
                  <p>Starting camera…</p>
                </div>
              )}

              {!photoPreview && cameraError && !uploading && (
                <div className={styles.photoPlaceholder}>
                  <CameraOff size={30} strokeWidth={1.5} />
                  <p>{cameraError}</p>
                </div>
              )}

              {uploading && (
                <div className={styles.uploadOverlay}>
                  <Upload size={30} strokeWidth={1.5} />
                  <p>Uploading photo…</p>
                </div>
              )}
            </div>

            {uploadError && <p className={styles.instruction}>{uploadError}</p>}

            <canvas ref={canvasRef} className={styles.hiddenInput} />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className={styles.hiddenInput}
              onChange={handleFile}
            />
          </main>

          <div className={cardStyles.ctaBar}>
            {photoPreview ? (
              <div className={styles.actionRow}>
                <Button
                  size="lg"
                  variant="outline"
                  icon={RotateCcw}
                  className={styles.retakeBtn}
                  disabled={uploading}
                  onClick={retake}
                >
                  Retake
                </Button>
                <Button
                  size="lg"
                  variant="primary"
                  icon={Check}
                  className={styles.submitBtn}
                  disabled={uploading}
                  onClick={submitPhoto}
                >
                  {uploading ? 'Uploading…' : 'Submit'}
                </Button>
              </div>
            ) : cameraError ? (
              <Button
                size="lg"
                variant="primary"
                icon={Upload}
                className={cardStyles.ctaBtn}
                onClick={openFilePicker}
              >
                Capture Photo
              </Button>
            ) : (
              <Button
                size="lg"
                variant="primary"
                icon={Camera}
                className={cardStyles.ctaBtn}
                disabled={!cameraReady}
                onClick={capturePhoto}
              >
                Take Photo
              </Button>
            )}
          </div>
        </>
      )}
    </DriverShell>
  );
}
